import React, { ReactNode, useState, useEffect, useMemo, useCallback } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { ConfigProvider } from 'antd';
import plPL from 'antd/locale/pl_PL';
import { ThemeContext } from './ThemeContext';
import { ThemeMode, AppTheme } from '../../styles/theme/theme.types';
import { lightColors, darkColors } from '../../styles/theme/colors';
import { getAntdTheme } from '../../styles/theme/antdTheme';
import { GlobalStyles } from '../../styles/GlobalStyles';

const THEME_STORAGE_KEY = 'proste-menu-theme';

interface ThemeContextProviderProps {
  children: ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved && ['light', 'dark', 'system'].includes(saved)) {
      return saved as ThemeMode;
    }
    return 'system';
  });

  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const isDark = useMemo(() => {
    if (themeMode === 'system') {
      return systemPrefersDark;
    }
    return themeMode === 'dark';
  }, [themeMode, systemPrefersDark]);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeMode(isDark ? 'light' : 'dark');
  }, [isDark, setThemeMode]);

  const theme: AppTheme = useMemo(() => ({
    mode: themeMode,
    colors: isDark ? darkColors : lightColors,
  }), [themeMode, isDark]);

  const antdTheme = useMemo(() => getAntdTheme(isDark), [isDark]);

  useEffect(() => {
    const htmlElement = document.documentElement;
    htmlElement.classList.remove('light', 'dark');
    htmlElement.classList.add(isDark ? 'dark' : 'light');
    htmlElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const contextValue = useMemo(() => ({
    themeMode,
    isDark,
    theme,
    setThemeMode,
    toggleTheme,
  }), [themeMode, isDark, theme, setThemeMode, toggleTheme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider locale={plPL} theme={antdTheme} renderEmpty={() => (
        <div style={{ padding: '32px 0', textAlign: 'center' }}>
          <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
            <g transform="translate(0 1)" fill="none" fillRule="evenodd">
              <ellipse fill="#f5f5f5" cx="32" cy="33" rx="32" ry="7" />
              <g fillRule="nonzero" stroke="#d9d9d9">
                <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z" />
                <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill="#fafafa" />
              </g>
            </g>
          </svg>
          <div style={{ color: '#999', fontSize: '14px', marginTop: '8px' }}>
            Brak danych
          </div>
        </div>
      )}>
        <StyledThemeProvider theme={theme}>
          <GlobalStyles />
          {children}
        </StyledThemeProvider>
      </ConfigProvider>
    </ThemeContext.Provider >
  );
};
